import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Loader2, Lock, Mail, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { api } from "../lib/api";

const slugifyKey = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_\s-]/g, "")
    .replace(/[\s-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);

const LOCKED = new Set(["email"]);

const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "email", label: "Email" },
  { value: "tel", label: "Phone number" },
  { value: "textarea", label: "Long text (multiline)" },
  { value: "select", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
];

const emptyField = (sort_order) => ({
  id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  key: "",
  label: "",
  type: "text",
  placeholder: "",
  help_text: "",
  required: false,
  enabled: true,
  sort_order,
  options: [],
});

const DEFAULT = {
  eyebrow: "Stay in the loop",
  heading: "Join the text & email list.",
  subheading: "",
  submit_label: "Stay Updated",
  consent_text: "",
  fields: [],
  confirmation: { eyebrow: "Thanks!", title: "", body: "", button_label: "Send another message" },
  notification: { enabled: false, recipients: [], subject_template: "New form submission from {name}" },
};

export const FormManager = () => {
  const [schema, setSchema] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [recipientsInput, setRecipientsInput] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/form");
        if (!cancelled) {
          setSchema(r.data);
          setRecipientsInput((r.data.notification?.recipients || []).join(", "));
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load form");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateSchema = (patch) => setSchema((s) => ({ ...s, ...patch }));
  const updateField = (id, patch) =>
    setSchema((s) => ({
      ...s,
      fields: s.fields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    }));

  const addField = () => {
    const f = emptyField(schema.fields.length);
    setSchema((s) => ({ ...s, fields: [...s.fields, f] }));
    setEditingFieldId(f.id);
  };

  const removeField = (id) => {
    const f = schema.fields.find((x) => x.id === id);
    if (!f) return;
    if (LOCKED.has(f.key)) {
      toast.error("The email field can't be removed.");
      return;
    }
    if (!window.confirm(`Remove the "${f.label || f.key}" field?`)) return;
    setSchema((s) => ({ ...s, fields: s.fields.filter((x) => x.id !== id) }));
  };

  const moveField = (id, dir) => {
    setSchema((s) => {
      const sorted = [...s.fields].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      const idx = sorted.findIndex((f) => f.id === id);
      if (idx < 0) return s;
      const target = dir === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= sorted.length) return s;
      [sorted[idx], sorted[target]] = [sorted[target], sorted[idx]];
      const renumbered = sorted.map((f, i) => ({ ...f, sort_order: i }));
      return { ...s, fields: renumbered };
    });
  };

  const parseRecipients = (input) =>
    input.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);

  const save = async (ev) => {
    ev.preventDefault();
    const recipients = parseRecipients(recipientsInput);
    if (schema.notification?.enabled && recipients.length === 0) {
      toast.error("Add at least one recipient email or turn notifications off.");
      return;
    }
    // Validate fields client-side
    for (const f of schema.fields) {
      if (!f.label?.trim() || !f.key?.trim()) {
        toast.error("Every field needs a label and key.");
        return;
      }
      if (!/^[a-z0-9_]+$/.test(f.key)) {
        toast.error(`Key "${f.key}" must use lowercase letters, numbers, or underscores only.`);
        return;
      }
      if (f.type === "select" && (!f.options || f.options.length === 0)) {
        toast.error(`Dropdown "${f.label}" needs at least one option.`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        eyebrow: schema.eyebrow || "",
        heading: schema.heading,
        subheading: schema.subheading || "",
        submit_label: schema.submit_label || "Submit",
        consent_text: schema.consent_text || "",
        fields: schema.fields.map((f, i) => ({
          ...f,
          sort_order: i,
          // strip tmp- ids so backend reassigns proper uuid
          id: f.id && !f.id.startsWith("tmp-") ? f.id : undefined,
          options: f.type === "select" ? (f.options || []) : [],
        })),
        confirmation: schema.confirmation,
        notification: {
          enabled: !!schema.notification?.enabled,
          recipients,
          subject_template: schema.notification?.subject_template || "New form submission from {name}",
        },
      };
      const r = await api.put("/admin/form", payload);
      setSchema(r.data);
      setRecipientsInput((r.data.notification?.recipients || []).join(", "));
      toast.success("Form saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-[#8D99AE] text-sm">Loading form…</div>;

  const sortedFields = [...schema.fields].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <form data-testid="form-manager" onSubmit={save} className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 space-y-6">
        {/* Copy section */}
        <section className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-8">
          <h3 className="font-display text-xl text-[#2C1E16]">Section copy</h3>
          <p className="text-sm text-[#5C4E46] mt-1">The header that sits above the form on your homepage.</p>

          <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-5">
            <Field label="Eyebrow (small caption)">
              <input data-testid="form-eyebrow" className="admin-input"
                value={schema.eyebrow || ""} onChange={(e) => updateSchema({ eyebrow: e.target.value })} />
            </Field>
            <Field label="Submit button label">
              <input data-testid="form-submit-label" className="admin-input"
                value={schema.submit_label || ""} onChange={(e) => updateSchema({ submit_label: e.target.value })} />
            </Field>
            <Field label="Heading" className="sm:col-span-2">
              <input data-testid="form-heading" className="admin-input"
                value={schema.heading || ""} onChange={(e) => updateSchema({ heading: e.target.value })} />
            </Field>
            <Field label="Subheading" className="sm:col-span-2">
              <textarea data-testid="form-subheading" rows={2} className="admin-input resize-none pt-2"
                value={schema.subheading || ""} onChange={(e) => updateSchema({ subheading: e.target.value })} />
            </Field>
            <Field label="Consent line (below the button)" className="sm:col-span-2">
              <input data-testid="form-consent" className="admin-input"
                value={schema.consent_text || ""} onChange={(e) => updateSchema({ consent_text: e.target.value })} />
            </Field>
          </div>
        </section>

        {/* Fields list */}
        <section className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-8">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="font-display text-xl text-[#2C1E16]">Form fields</h3>
              <p className="text-sm text-[#5C4E46] mt-1">Add, rename, reorder, or remove fields. The Email field is locked because it's how we identify your subscribers.</p>
            </div>
            <button
              type="button"
              onClick={addField}
              data-testid="form-add-field"
              className="inline-flex items-center gap-2 bg-[#2C1E16] text-[#FAF9F6] rounded-full px-4 py-2 text-sm hover:bg-[#3E2D24]"
            >
              <Plus size={14} strokeWidth={1.6} />
              Add field
            </button>
          </div>

          <div className="mt-4 space-y-3" data-testid="form-fields-list">
            {sortedFields.map((f, i) => (
              <FieldRow
                key={f.id}
                field={f}
                editing={editingFieldId === f.id}
                onEdit={(open) => setEditingFieldId(open ? f.id : null)}
                onChange={(patch) => updateField(f.id, patch)}
                onRemove={() => removeField(f.id)}
                onMoveUp={() => moveField(f.id, "up")}
                onMoveDown={() => moveField(f.id, "down")}
                isFirst={i === 0}
                isLast={i === sortedFields.length - 1}
              />
            ))}
            {sortedFields.length === 0 && (
              <p className="text-sm text-[#8D99AE]">No fields yet. Add one above.</p>
            )}
          </div>
        </section>

        {/* Confirmation message */}
        <section className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-8">
          <h3 className="font-display text-xl text-[#2C1E16]">Confirmation message</h3>
          <p className="text-sm text-[#5C4E46] mt-1">Shown right after someone submits the form.</p>

          <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-5">
            <Field label="Eyebrow">
              <input data-testid="form-conf-eyebrow" className="admin-input"
                value={schema.confirmation?.eyebrow || ""}
                onChange={(e) => updateSchema({ confirmation: { ...schema.confirmation, eyebrow: e.target.value } })} />
            </Field>
            <Field label="Button text (to send another message)">
              <input data-testid="form-conf-button" className="admin-input"
                value={schema.confirmation?.button_label || ""}
                onChange={(e) => updateSchema({ confirmation: { ...schema.confirmation, button_label: e.target.value } })} />
            </Field>
            <Field label="Title" className="sm:col-span-2">
              <input data-testid="form-conf-title" className="admin-input"
                value={schema.confirmation?.title || ""}
                onChange={(e) => updateSchema({ confirmation: { ...schema.confirmation, title: e.target.value } })} />
            </Field>
            <Field label="Body" className="sm:col-span-2">
              <textarea data-testid="form-conf-body" rows={3} className="admin-input resize-none pt-2"
                value={schema.confirmation?.body || ""}
                onChange={(e) => updateSchema({ confirmation: { ...schema.confirmation, body: e.target.value } })} />
            </Field>
          </div>
        </section>

        {/* Notifications */}
        <section className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <Mail size={18} strokeWidth={1.5} className="mt-1 text-[#2C1E16]" />
            <div className="flex-1">
              <h3 className="font-display text-xl text-[#2C1E16]">Email notifications</h3>
              <p className="text-sm text-[#5C4E46] mt-1">
                Get an email when someone submits the form. Reply-To is set to the submitter so you can hit Reply directly.
              </p>
            </div>
          </div>

          <label className="mt-6 inline-flex items-center gap-3 cursor-pointer select-none">
            <input
              data-testid="form-notify-enabled"
              type="checkbox"
              checked={!!schema.notification?.enabled}
              onChange={(e) => updateSchema({ notification: { ...schema.notification, enabled: e.target.checked } })}
              className="h-4 w-4 accent-[#2C1E16]"
            />
            <span className="text-sm text-[#2C1E16]">Send a notification email on every new submission</span>
          </label>

          <div className="mt-5 grid gap-5">
            <Field label="Recipient emails (comma-separated)">
              <input
                data-testid="form-notify-recipients"
                className="admin-input"
                value={recipientsInput}
                onChange={(e) => setRecipientsInput(e.target.value)}
                placeholder="hello@memphiscoffee.co, partner@memphiscoffee.co"
              />
              <span className="block text-[11px] text-[#8D99AE] mt-1">
                Tip: if you're still using <code>onboarding@resend.dev</code> as the sender, Resend will only deliver to verified addresses. Verify your domain in Resend to send to any recipient.
              </span>
            </Field>
            <Field label="Subject template">
              <input
                data-testid="form-notify-subject"
                className="admin-input"
                value={schema.notification?.subject_template || ""}
                onChange={(e) =>
                  updateSchema({
                    notification: { ...schema.notification, subject_template: e.target.value },
                  })
                }
                placeholder="New form submission from {name}"
              />
              <span className="block text-[11px] text-[#8D99AE] mt-1">
                Available placeholders: <code>{"{name}"}</code> and <code>{"{email}"}</code>.
              </span>
            </Field>
          </div>
        </section>

        <div className="flex justify-end">
          <button
            type="submit"
            data-testid="form-save"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#2C1E16] text-[#FAF9F6] hover:bg-[#3E2D24] disabled:opacity-60 transition-colors rounded-full px-6 py-3 font-medium"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} strokeWidth={1.6} />}
            Save form
          </button>
        </div>
      </div>

      {/* Live preview */}
      <aside className="lg:col-span-4">
        <div className="sticky top-24 rounded-3xl bg-[#2C1E16] text-[#FAF9F6] p-6 shadow-[0_18px_50px_rgb(0,0,0,0.18)]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4A373]">Preview</div>
          <div className="mt-3">
            {schema.eyebrow && <div className="text-[10px] uppercase tracking-[0.16em] text-[#D4A373]">{schema.eyebrow}</div>}
            <div className="font-display text-xl mt-1 leading-tight">{schema.heading}</div>
            {schema.subheading && <div className="text-xs text-[#FAF9F6]/70 mt-1">{schema.subheading}</div>}
          </div>
          <div className="mt-5 space-y-3">
            {sortedFields.filter((f) => f.enabled).map((f) => (
              <div key={f.id} className="text-[11px] text-[#FAF9F6]/80 border-b border-[#FAF9F6]/15 pb-1">
                {f.label}{f.required && <span className="text-[#D4A373]"> *</span>}
                <span className="ml-2 text-[10px] text-[#FAF9F6]/40 uppercase tracking-wider">{f.type}</span>
              </div>
            ))}
          </div>
          <button type="button" disabled className="mt-5 w-full bg-[#FAF9F6] text-[#2C1E16] rounded-full px-4 py-2 text-sm">
            {schema.submit_label || "Submit"}
          </button>
          {schema.consent_text && <div className="text-[10px] text-[#FAF9F6]/45 mt-3">{schema.consent_text}</div>}

          <div className="mt-6 pt-5 border-t border-[#FAF9F6]/15">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#D4A373]">After submitting</div>
            {schema.confirmation?.eyebrow && (
              <div className="text-[#D4A373] text-sm mt-2 font-medium">{schema.confirmation.eyebrow}</div>
            )}
            {schema.confirmation?.title && <div className="font-display text-lg mt-1">{schema.confirmation.title}</div>}
            {schema.confirmation?.body && <div className="text-xs text-[#FAF9F6]/70 mt-1">{schema.confirmation.body}</div>}
          </div>
        </div>
      </aside>
    </form>
  );
};

const FieldRow = ({ field, editing, onEdit, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const locked = LOCKED.has(field.key);
  return (
    <div data-testid={`form-field-row-${field.key || field.id}`} className="rounded-2xl border border-[#E8E3DD] bg-[#FAF9F6]/40">
      <div className="px-4 py-3 flex items-center gap-2">
        <div className="flex flex-col gap-0.5">
          <button type="button" onClick={onMoveUp} disabled={isFirst} className="p-0.5 disabled:opacity-25 text-[#8D99AE] hover:text-[#2C1E16]" title="Move up">
            <ArrowUp size={13} strokeWidth={1.7} />
          </button>
          <button type="button" onClick={onMoveDown} disabled={isLast} className="p-0.5 disabled:opacity-25 text-[#8D99AE] hover:text-[#2C1E16]" title="Move down">
            <ArrowDown size={13} strokeWidth={1.7} />
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-[#2C1E16] truncate">{field.label || "(no label)"}</span>
            {locked && <Lock size={12} strokeWidth={1.6} className="text-[#8D99AE]" title="Locked" />}
            {field.required && <span className="text-[10px] uppercase tracking-[0.14em] text-[#D4A373]">Required</span>}
            {!field.enabled && <span className="text-[10px] uppercase tracking-[0.14em] text-[#8D99AE]">Hidden</span>}
          </div>
          <div className="text-[11px] text-[#8D99AE] mt-0.5">
            <span className="uppercase tracking-wider">{field.type}</span> · key: <code>{field.key || "?"}</code>
          </div>
        </div>
        <button type="button" onClick={() => onEdit(!editing)} data-testid={`form-field-edit-${field.key || field.id}`} className="p-2 rounded-full hover:bg-white text-[#2C1E16]">
          {editing ? <X size={15} strokeWidth={1.6} /> : <Pencil size={14} strokeWidth={1.6} />}
        </button>
        <button
          type="button"
          onClick={onRemove}
          disabled={locked}
          data-testid={`form-field-remove-${field.key || field.id}`}
          className="p-2 rounded-full hover:bg-white text-[#B33A3A] disabled:opacity-30 disabled:cursor-not-allowed"
          title={locked ? "Required field" : "Remove"}
        >
          <Trash2 size={14} strokeWidth={1.6} />
        </button>
      </div>

      {editing && (
        <div className="border-t border-[#E8E3DD] px-4 py-5 grid sm:grid-cols-2 gap-x-5 gap-y-4 bg-white rounded-b-2xl">
          <Field label="Label (what people see)">
            <input
              data-testid={`form-field-label-${field.id}`}
              className="admin-input"
              value={field.label}
              onChange={(e) => {
                const label = e.target.value;
                // Auto-suggest a key for new fields without one (skip locked + once user typed a key)
                const next = { label };
                if (!locked && (!field.key || field.key === slugifyKey(field.label))) {
                  next.key = slugifyKey(label);
                }
                onChange(next);
              }}
              placeholder="e.g., Catering date"
            />
          </Field>
          <Field label="Field key (stored in database)">
            <input
              data-testid={`form-field-key-${field.id}`}
              className="admin-input disabled:opacity-60"
              value={field.key}
              disabled={locked}
              onChange={(e) => onChange({ key: slugifyKey(e.target.value) })}
              placeholder="catering_date"
            />
            <span className="block text-[10px] text-[#8D99AE] mt-1">
              {locked ? "Locked — this field identifies your subscribers." : "Lowercase letters, numbers, underscores only."}
            </span>
          </Field>
          <Field label="Type">
            <select
              data-testid={`form-field-type-${field.id}`}
              className="admin-input disabled:opacity-60"
              value={field.type}
              disabled={locked}
              onChange={(e) => onChange({ type: e.target.value })}
            >
              {FIELD_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Placeholder">
            <input
              data-testid={`form-field-placeholder-${field.id}`}
              className="admin-input"
              value={field.placeholder || ""}
              onChange={(e) => onChange({ placeholder: e.target.value })}
              placeholder="Shown in the empty field"
            />
          </Field>
          <Field label="Help text (small caption under field)" className="sm:col-span-2">
            <input
              data-testid={`form-field-help-${field.id}`}
              className="admin-input"
              value={field.help_text || ""}
              onChange={(e) => onChange({ help_text: e.target.value })}
            />
          </Field>

          {field.type === "select" && (
            <div className="sm:col-span-2">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#8D99AE] mb-2">Dropdown options</div>
              <div className="space-y-2">
                {(field.options || []).map((opt, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      data-testid={`form-field-option-label-${field.id}-${i}`}
                      className="admin-input"
                      value={opt.label}
                      onChange={(e) => {
                        const options = [...(field.options || [])];
                        options[i] = { ...options[i], label: e.target.value, value: options[i].value || slugifyKey(e.target.value) };
                        onChange({ options });
                      }}
                      placeholder="Catering"
                    />
                    <input
                      data-testid={`form-field-option-value-${field.id}-${i}`}
                      className="admin-input"
                      value={opt.value}
                      onChange={(e) => {
                        const options = [...(field.options || [])];
                        options[i] = { ...options[i], value: slugifyKey(e.target.value) };
                        onChange({ options });
                      }}
                      placeholder="catering"
                    />
                    <button
                      type="button"
                      onClick={() => onChange({ options: field.options.filter((_, j) => j !== i) })}
                      className="text-[#B33A3A] p-2 hover:bg-[#FAF9F6] rounded-full"
                    >
                      <Trash2 size={14} strokeWidth={1.6} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => onChange({ options: [...(field.options || []), { label: "", value: "" }] })}
                  data-testid={`form-field-option-add-${field.id}`}
                  className="inline-flex items-center gap-1 text-xs text-[#2C1E16] hover:text-[#5C4E46]"
                >
                  <Plus size={12} strokeWidth={1.7} /> Add option
                </button>
              </div>
            </div>
          )}

          <label className="sm:col-span-1 inline-flex items-center gap-3 cursor-pointer select-none">
            <input
              data-testid={`form-field-required-${field.id}`}
              type="checkbox"
              checked={!!field.required}
              disabled={locked}
              onChange={(e) => onChange({ required: e.target.checked })}
              className="h-4 w-4 accent-[#2C1E16]"
            />
            <span className="text-sm text-[#2C1E16]">Required</span>
          </label>
          <label className="sm:col-span-1 inline-flex items-center gap-3 cursor-pointer select-none">
            <input
              data-testid={`form-field-enabled-${field.id}`}
              type="checkbox"
              checked={!!field.enabled}
              disabled={locked}
              onChange={(e) => onChange({ enabled: e.target.checked })}
              className="h-4 w-4 accent-[#2C1E16]"
            />
            <span className="text-sm text-[#2C1E16]">Visible on form</span>
          </label>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#8D99AE] mb-1">{label}</span>
    {children}
  </label>
);
